import { createSelector } from 'reselect';
import { omit } from 'lodash';
import { getDefaultProposal } from 'Utils/proposal/proposalUtils';
import { userInfoSelector } from 'Store/user-info/userInfo.selectors';
import { tripInformationSelector } from 'Store/trip-information/storeHelper';
import { selectAttendeesWithInfosByHouseholds } from './attendees/attendees.selector';

const bookingSelector = ({ booking }) => booking || {};

const additionalTransportsSelector = createSelector(
  bookingSelector,
  ({ additionalTransports }) => additionalTransports || {},
);
const getCityTransportsSelector = createSelector(
  bookingSelector,
  ({ additionalTransports }) => additionalTransports.transports.cityTransports || {},
);
const getPaymentProviders = createSelector(
  bookingSelector,
  ({ paymentProviders }) => paymentProviders,
);
const getInsurancesStore = createSelector(bookingSelector, ({ insurance }) => {
  return insurance;
});
const getSearchCriteriaTransports = createSelector(
  additionalTransportsSelector,
  ({ searchCriteria }) => searchCriteria,
);
const getSearchCriteriaTransportsInbound = createSelector(
  additionalTransportsSelector,
  ({ searchCriteria }) => searchCriteria && searchCriteria[0],
);
const getAdditionalTransports = createSelector(
  additionalTransportsSelector,
  ({ transports }) => transports,
);
const getAdditionalTransportsError = createSelector(
  additionalTransportsSelector,
  ({ error }) => error,
);
const getIsLoadingAdditionalTransports = createSelector(
  additionalTransportsSelector,
  ({ isLoading }) => isLoading,
);
const additionalAccommodationsSelector = createSelector(
  bookingSelector,
  ({ additionalAccommodations }) => additionalAccommodations || {},
);
const getIsLoadingAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ isLoading }) => isLoading,
);
const getIsLoadingFilteredAccommodationsAlternativesByComfort = createSelector(
  additionalAccommodationsSelector,
  ({ isLoadingFilteredAccommodationsAlternativesByComfort }) =>
    isLoadingFilteredAccommodationsAlternativesByComfort,
);
const getAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ accommodationsSets }) => accommodationsSets,
);
const getAdditionalAccommodationsFilters = createSelector(
  additionalAccommodationsSelector,
  ({ filters }) => filters,
);
const getAdditionalAccommodationsPmrChecked = createSelector(
  additionalAccommodationsSelector,
  ({ isPmrChecked }) => isPmrChecked,
);
const getAdditionalAccommodationsError = createSelector(
  additionalAccommodationsSelector,
  ({ error }) => error,
);
const getFilteredAdditionalAccommodations = createSelector(
  additionalAccommodationsSelector,
  ({ filteredAccommodations }) => filteredAccommodations,
);
const getFilteredAdditionalAccommodationsSets = createSelector(
  getFilteredAdditionalAccommodations,
  getAdditionalAccommodations,
  (filteredAccommodations, additionalAccommodations) => {
    return filteredAccommodations && filteredAccommodations.length > 0
      ? filteredAccommodations
      : additionalAccommodations;
  },
);
const getFilteredAccommodationsAlternativesByComfort = createSelector(
  additionalAccommodationsSelector,
  ({ filteredAccommodationsAlternativesByComfort }) => filteredAccommodationsAlternativesByComfort,
);
const proposalsSelector = createSelector(bookingSelector, ({ proposals }) => proposals || []);
const getCurrentResort = createSelector(bookingSelector, ({ resort }) => resort);

const findAttendeeByNeolid = (attendees, neolid) =>
  attendees.find((attendee) => attendee.neolid === neolid);

const getSelectedProposalId = createSelector(
  bookingSelector,
  ({ selectedProposalId }) => selectedProposalId,
);

/**
 * It should be getSelectedProposal but there is a selector getSelectedProposal
 * witch get the proposal from paymentInfoSelector
 * I do not know why...
 */
const getCurrentProposal = createSelector(
  bookingSelector,
  ({ proposals, selectedProposalId }) =>
    /**
     * return the selected proposal if found,
     * if not (for any reason) the default proposal for keeping the return shape value consistent
     */
    proposals.find((proposal) => proposal.id === selectedProposalId) ||
    getDefaultProposal(proposals),
);
const getAllChildcaresByProductInfoId = createSelector(
  getCurrentProposal,
  (state, productInfoId) => productInfoId,
  ({ additionalServices }, productInfoId) =>
    additionalServices.childcares.filter(({ pricingInfo: { id } }) => id === productInfoId),
);
const getCurrentTotalPrice = createSelector(
  getCurrentProposal,
  (proposal) => proposal.price.total.value,
);

const filterProposalBySamePackage = (
  proposals: Array<Proposal>,
  selectedProposal: Proposal,
  resort: Readonly<ProductWithAccommodations> | null | undefined,
): Array<Proposal> => {
  const errorsProposals = proposals.filter((proposal) => proposal.errorCode);

  if (errorsProposals.length) {
    return errorsProposals;
  }

  // todo add the urban oasis condition once the feature is merged and ofc remove this comment 🤷‍ (Souhail)
  return resort?.isJoyview || resort?.isUrbanOasis
    ? proposals.filter((item) => {
        const { accommodations = [], packageId } = item;
        return accommodations.length && packageId === selectedProposal.packageId;
      })
    : proposals.filter((item) => {
        const { accommodations = [] } = item;
        return accommodations.length;
      });
};

const getProposals = createSelector(
  proposalsSelector,
  getCurrentProposal,
  getCurrentResort,
  filterProposalBySamePackage,
);
const getProposalsWithMappedAttendees = createSelector(
  getProposals,
  tripInformationSelector,
  (proposals, tripInformation) => {
    if (!proposals) {
      return proposals;
    }

    return proposals.map((proposal) => {
      return {
        ...proposal,
        attendees: {
          ...proposal.attendees,
          list: proposal.attendees.list.map((attendee) => {
            // if there is a birthdate it's a children otherwise it's an adult
            const attendeeData = attendee.birthdate
              ? findAttendeeByNeolid(tripInformation.kids, attendee.customerId)
              : findAttendeeByNeolid(tripInformation.adults, attendee.customerId);

            if (!attendeeData) {
              return attendee;
            }

            return {
              ...attendee,
              firstName: attendeeData.firstName,
              lastName: attendeeData.lastName,
            };
          }),
        },
      };
    });
  },
);
const getProposalHasAnyEquipmentForRent = createSelector(
  bookingSelector,
  ({ rentalEquipments }) => rentalEquipments.hasAnyEquipmentForRent,
);
const getConnectedUserInfoSelector = createSelector(userInfoSelector, (connectedUserInfo) =>
  connectedUserInfo.isQuickLoggedIn
    ? {}
    : omit(
        {
          ...connectedUserInfo,
          birthdate: connectedUserInfo.dateOfBirth,
          neolid: connectedUserInfo.neolaneId,
          // harmonize the neolid between user and seller
          mobile: connectedUserInfo.mobilePhoneNumber,
        },
        [
          'dateOfBirth',
          'GMNumber',
          'homePhoneNumber',
          'isProspect',
          'neolaneId',
          'unicodeCivility',
          'expirationDate',
          'mobilePhoneNumber',
        ],
      ),
);
const getStreetViews = createSelector(bookingSelector, ({ streetViews }) => streetViews);
const getSuggestions = createSelector(bookingSelector, ({ suggestions }) => suggestions);
type AdultChildPassportType = {
  expirationDate?: string;
  documentNumber?: string;
  issuingDate?: string;
  nationality?: string;
  issuingCountry?: string;
  birthCountry?: string;
  birthCity?: string;
  secondName?: string;
  thirdName?: string;
  fourthName?: string;
  type?: string;
};
type AttendeeWithInfosType = {
  id?: string;
  isMain?: boolean;
  isAdult?: boolean;
  neolid?: string;
  customerId?: string;
  civility?: string;
  firstName?: string;
  lastName?: string;
  unicodeFirstName?: string;
  unicodeLastName?: string;
  attendeeIndex?: number;
  gmNumber?: string;
  householdId?: number;
  document?: Readonly<{
    passport?: AdultChildPassportType;
    idcard?: AdultChildPassportType;
  }>;
};
export type AdultAttendeeWithInfosType = Readonly<
  AttendeeWithInfosType & {
    email?: string;
    mobile?: string;
    birthdate?: number | null | undefined;
    identityNumber?: string;
    address?: Readonly<{
      additionalInformation1?: string;
      additionalInformation2?: string;
      addOn?: string;
      city?: string;
      country?: string;
      countryCode?: string;
      stateOrDistrict?: string;
      streetName?: string;
      streetNumber?: string;
      town?: string;
      zipCode?: string;
    }>;
    loyaltyProgram?: {
      status: string | null | undefined;
      points: number | null | undefined;
    };
    optinEmail?: boolean | null | undefined;
    optinSms?: boolean | null | undefined;
    optinMail?: boolean | null | undefined;
    optinFosun?: boolean | null | undefined;
    optinPhone?: boolean | null | undefined;
    optinClubmedPlus?: boolean | null | undefined;
  }
>;
export type ChildAttendeeWithInfosType = Readonly<
  AttendeeWithInfosType & {
    birthdate: number | null | undefined;
    dateOfBirth?: number | null | undefined;
  }
>;
export type AttendeesWithInfosType = {
  adults: ReadonlyArray<AdultAttendeeWithInfosType>;
  children: ReadonlyArray<ChildAttendeeWithInfosType>;
};
export const getCustomerSearchResult = createSelector(
  bookingSelector,
  (booking) => booking.customerSearch,
);
const getReservedServicesByAttendee = createSelector(
  getCurrentProposal,
  (state) => selectAttendeesWithInfosByHouseholds(state).attendees,
  ({ additionalServices }, attendeesWithInfos): ReadonlyArray<ReservedServicesByAttendee> => {
    // defensive code if store is not available (ex: confirmation page)
    if (!additionalServices || !attendeesWithInfos) {
      return [];
    }

    const attendeesWithServiceRented = [
      ...additionalServices.activities,
      ...additionalServices.rentals,
    ].reduce((acc, service) => {
      const { pricingInfo, productInfo } = service;
      pricingInfo.attendees.forEach((attendee) => {
        const product = {
          price: attendee.rentedAmount.value,
          services: [service],
        };

        if (attendee.rentedAmount.value > 0) {
          if (!acc[attendee.id]) {
            acc[attendee.id] = {
              [productInfo.id]: { ...product },
            };
          } else if (!acc[attendee.id][productInfo.id]) {
            acc[attendee.id][productInfo.id] = { ...product };
          } else {
            acc[attendee.id][productInfo.id].services.push(service);
          }
        }
      });
      return acc;
    }, {});
    return Object.entries(attendeesWithServiceRented)
      .reduce((reservedServicesByAttendee, [id, rentedServices]) => {
        const childIndex = attendeesWithInfos.children.findIndex((attendee) => attendee.id === id);

        if (childIndex > -1) {
          return [
            ...reservedServicesByAttendee,
            {
              ...attendeesWithInfos.children[childIndex],
              id,
              index: childIndex + 1,
              isAdult: false,
              rentedServices: Object.values(rentedServices),
            },
          ];
        }

        const adultIndex = attendeesWithInfos.adults.findIndex((attendee) => attendee.id === id);
        return [
          ...reservedServicesByAttendee,
          {
            ...attendeesWithInfos.adults[adultIndex],
            id,
            index: adultIndex + 1,
            isAdult: true,
            rentedServices: Object.values(rentedServices),
          },
        ];
      }, [])
      .sort((a, b) => {
        if (a.isAdult && !b.isAdult) {
          return -1;
        }

        if (!a.isAdult && b.isAdult) {
          return 1;
        }

        return a.index - b.index;
      });
  },
);

const getDetailedAttendees = ({ booking }: { booking: Booking }): BookingAttendeesType =>
  booking.attendees;

export {
  filterProposalBySamePackage,
  getAdditionalTransportsError,
  getAllChildcaresByProductInfoId,
  getCityTransportsSelector,
  getIsLoadingAdditionalAccommodations,
  getFilteredAccommodationsAlternativesByComfort,
  getIsLoadingFilteredAccommodationsAlternativesByComfort,
  getIsLoadingAdditionalTransports,
  getProposals,
  getAdditionalAccommodations,
  getAdditionalAccommodationsFilters,
  getAdditionalAccommodationsPmrChecked,
  getAdditionalAccommodationsError,
  getFilteredAdditionalAccommodations,
  getFilteredAdditionalAccommodationsSets,
  getAdditionalTransports,
  getSearchCriteriaTransports,
  getSearchCriteriaTransportsInbound,
  getReservedServicesByAttendee,
  getSelectedProposalId,
  getCurrentResort,
  getPaymentProviders,
  getProposalsWithMappedAttendees,
  getCurrentProposal,
  getCurrentTotalPrice,
  getProposalHasAnyEquipmentForRent,
  getDetailedAttendees,
  getConnectedUserInfoSelector,
  proposalsSelector,
  getSuggestions,
  getInsurancesStore,
  getStreetViews,
};
